import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import api from '../services/api';
import { clearQueryCache } from '../lib/queryClient';

// Tier 1 (Vik 2026-11-02 22:00 PT): the server says where a fresh session goes first (an Admin of a workspace with unconfirmed
// retention classes goes to the retention screen). Read once by the login screen.
export const ONBOARDING_NEXT_KEY = 'onboarding_next';
function rememberOnboardingNext(data) {
  try {
    if (data && data.onboarding_next) sessionStorage.setItem(ONBOARDING_NEXT_KEY, data.onboarding_next);
    else sessionStorage.removeItem(ONBOARDING_NEXT_KEY);
  } catch (e) { /* storage unavailable: the dashboard opens */ }
}

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const API_BASE_URL = config.API_BASE_URL.replace('/api/v1', '');

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- EFFECT 1: One-time setup (mount only) ---
  // Load user from cookie session, set up session-expired listener, tenant interceptor
  useEffect(() => {
    // Listen for session-expired events from api.js interceptor
    const handleSessionExpired = () => {
      setError('session-expired');
      setUser(null);
      localStorage.removeItem('tenant_id');
      clearQueryCache(); // never keep cached tenant data past the session
    };
    window.addEventListener('session-expired', handleSessionExpired);

    // Tenant Context Request Interceptor (for global axios calls)
    const requestInterceptor = axios.interceptors.request.use((config) => {
      const match = window.location.pathname.match(/\/t\/([^/]+)/);
      if (match && match[1]) {
        config.headers['X-Target-Tenant-ID'] = match[1];
      }
      return config;
    });

    // Load user from existing cookie session (runs ONCE on mount)
    const loadUser = async () => {
      const timeout = setTimeout(() => {
        console.warn("[AuthContext] Loading timeout — clearing session");
        setUser(null);
        setLoading(false);
      }, 10000);
      try {
        // withCredentials sends cookie automatically
        const response = await api.get('/users/me');
        clearTimeout(timeout);
        setUser(response.data);
        // Persist tenant_id (non-sensitive routing helper — NOT a token)
        if (response.data?.tenant_id) {
          localStorage.setItem('tenant_id', response.data.tenant_id);
        }
      } catch (err) {
        clearTimeout(timeout);
        // No valid session — user needs to login (not an error to log)
        setUser(null);
      }
      setLoading(false);
    };

    loadUser();

    // Cleanup
    return () => {
      window.removeEventListener('session-expired', handleSessionExpired);
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, []); // Empty dependency — runs once on mount only

  // --- EFFECT 2: Inactivity timer (re-binds when user changes) ---
  // Warning at 14 min, forced logout at 15 min
  useEffect(() => {
    if (!user) return; // No timer needed if not logged in

    const WARNING_MS = 55 * 60 * 1000;  // 55 minutes — show warning modal
    const LOGOUT_MS = 60 * 60 * 1000;   // 60 minutes — force logout
    let warningTimer;
    let logoutTimer;

    const resetInactivityTimer = () => {
      clearTimeout(warningTimer);
      clearTimeout(logoutTimer);
      // Dismiss any active warning modal
      window.dispatchEvent(new CustomEvent('session-activity'));

      warningTimer = setTimeout(() => {
        console.warn("[AuthContext] 14 min idle — showing session warning");
        window.dispatchEvent(new CustomEvent('session-warning'));
      }, WARNING_MS);

      logoutTimer = setTimeout(() => {
        console.warn("[AuthContext] 15 min idle — forcing logout");
        logout();
        window.location.href = '/login?reason=timeout';
      }, LOGOUT_MS);
    };

    const activityEvents = ['mousedown', 'keydown', 'mousemove', 'scroll', 'touchstart'];
    const handleUserActivity = () => resetInactivityTimer();

    // Listen for keep-alive from the modal "Stay Logged In" button
    const handleKeepAlive = () => resetInactivityTimer();
    window.addEventListener('session-keep-alive', handleKeepAlive);

    activityEvents.forEach(event => document.addEventListener(event, handleUserActivity, { passive: true }));
    resetInactivityTimer();

    return () => {
      clearTimeout(warningTimer);
      clearTimeout(logoutTimer);
      window.removeEventListener('session-keep-alive', handleKeepAlive);
      activityEvents.forEach(event => document.removeEventListener(event, handleUserActivity));
    };
  }, [user]); // Re-bind timer when user changes

  // --- EFFECT 3: Proactive token refresh every 25 minutes ---
  useEffect(() => {
    if (!user) return;

    const REFRESH_INTERVAL_MS = 25 * 60 * 1000; // 25 minutes
    const refreshInterval = setInterval(async () => {
      try {
        await api.post('/auth/refresh');
        console.debug("[AuthContext] Proactive token refresh succeeded");
      } catch (err) {
        console.warn("[AuthContext] Proactive token refresh failed:", err?.response?.status);
      }
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(refreshInterval);
  }, [user]);

  const login = async (username, password, tenantId = null) => {
    setError(null);
    console.log(`[AuthContext] Attempting login for: ${username} (Tenant: ${tenantId || 'Global'})`);
    try {
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);

      const reqConfig = {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest',
        },
        withCredentials: true,  // Receive HttpOnly cookies from login response
      };

      if (tenantId) {
        reqConfig.headers['X-Target-Tenant-ID'] = tenantId;
      }

      console.log(`[AuthContext] Sending POST to ${API_BASE_URL}/api/v1/auth/login...`);
      const response = await axios.post(`${API_BASE_URL}/api/v1/auth/login`, formData, reqConfig);

      console.log("[AuthContext] Login response received.");

      const { mfa_required, mfa_enroll_required, mfa_token, must_enroll_mfa } = response.data;

      if (mfa_required) {
        console.log("[AuthContext] MFA Required.");
        // No session yet — carry the short-lived pending token to the next step.
        // mfa_enroll_required = forced enrollment (grace expired); otherwise a
        // normal TOTP challenge for an already-enrolled user.
        return {
          success: true,
          mfaRequired: true,
          mfaEnrollRequired: !!mfa_enroll_required,
          mfaToken: mfa_token,
        };
      }

      // Session set via cookie. Fetch profile to update context.
      rememberOnboardingNext(response.data);
      const userData = await fetchUserProfile();
      // Within-grace nudge: enforcement is on but the user hasn't enrolled yet.
      return { success: true, user: userData, mustEnrollMfa: !!must_enroll_mfa };
    } catch (err) {
      console.error("[AuthContext] Login process failed:", err);

      let msg = err.response?.data?.detail
        || err.message
        || "Login failed (Unknown Error)";

      if (typeof msg === 'object') {
        try { msg = JSON.stringify(msg); } catch (e) { msg = "Login failed"; }
      }
      console.error("[AuthContext] Error message:", msg);
      setError(msg);
      return { success: false, error: msg };
    }
  };

  const verifyMfa = async (code, pendingToken) => {
    setError(null);
    try {
      // Exchange the pending token + code for a real session (sets cookies).
      const res = await axios.post(`${API_BASE_URL}/api/v1/auth/login/verify-totp`,
        { mfa_token: pendingToken, code },
        { withCredentials: true });
      rememberOnboardingNext(res.data);
      await fetchUserProfile();
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.detail || "MFA Verification Failed";
      setError(msg);
      return { success: false, error: msg };
    }
  };

  // Forced-enrollment during login (grace expired). Uses the purpose=mfa_enroll
  // pending token; no session exists until confirm succeeds.
  const loginEnrollStart = async (pendingToken) => {
    const res = await axios.post(`${API_BASE_URL}/api/v1/auth/login/enroll-mfa`,
      { mfa_token: pendingToken }, { withCredentials: true });
    return res.data;  // { otpauth_uri, secret }
  };

  const loginEnrollConfirm = async (pendingToken, code) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/v1/auth/login/confirm-mfa`,
        { mfa_token: pendingToken, code }, { withCredentials: true });
      rememberOnboardingNext(res.data);
      await fetchUserProfile();  // session now set
      return { success: true, backupCodes: res.data.backup_codes };
    } catch (err) {
      const msg = err.response?.data?.detail || "Enrollment failed";
      setError(msg);
      return { success: false, error: msg };
    }
  };

  const fetchUserProfile = async () => {
    console.log("[AuthContext] Fetching user profile (/users/me)...");
    const userResponse = await api.get('/users/me');
    console.log("[AuthContext] User profile received.");
    setUser(userResponse.data);
    if (userResponse.data?.tenant_id) {
      localStorage.setItem('tenant_id', userResponse.data.tenant_id);
    }
    return userResponse.data;
  };

  const logout = async () => {
    console.log("[AuthContext] Logging out...");
    // Clear local state FIRST to prevent any re-fetch race condition
    setUser(null);
    setError(null);
    localStorage.removeItem('tenant_id');
    clearQueryCache(); // wipe cached compliance data on logout
    try {
      // Server-side logout: revoke refresh token + clear cookies
      await api.post('/auth/logout');
    } catch (err) {
      // Best-effort — even if server call fails, cookies will expire naturally
      console.warn("[AuthContext] Server logout failed:", err);
    }
  };

  const value = {
    user,
    login,
    verifyMfa,
    loginEnrollStart,
    loginEnrollConfirm,
    logout,
    loading,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
